import React from "react";

import { Button } from "./ui/Button";

/**
 * MOSA-908. The first screen when there is no Sheet link: paste, Drive, or
 * sample. One main action per choice. PostgreSQL stays under a quiet link.
 */

export type SheetsConnectChoice = "paste" | "browse" | "sample";

export type SheetsConnectChoicesProps = {
  onChoose: (choice: SheetsConnectChoice) => void;
  onMoreConnections: () => void;
  /** Hide "Try a sample sheet" after the API returns sample_sheet_unavailable. */
  sampleAvailable?: boolean;
  sampleLoading?: boolean;
  /** Optional title override for the data-sources empty state. */
  title?: string;
  /** Optional supporting sentence. */
  detail?: string;
  /** Where this block is rendered, for tests. */
  testId?: string;
  /**
   * When true, drop the outer card chrome. The parent already provides the
   * bordered surface (SheetsConnect choice screen), so a second card reads as clutter.
   */
  embedded?: boolean;
};

const SheetsConnectChoices: React.FC<SheetsConnectChoicesProps> = ({
  onChoose,
  onMoreConnections,
  sampleAvailable = true,
  sampleLoading = false,
  title = "Connect a Google Sheet",
  detail = "Paste a link. Choose a file from Drive. Or try a sample sheet.",
  testId = "sheets-connect-choices",
  embedded = false,
}) => {
  return (
    <section
      className={
        embedded
          ? undefined
          : "rounded-lg border border-od-border bg-od-surface p-4 md:p-6"
      }
      data-testid={testId}
      aria-labelledby={`${testId}-title`}
    >
      <h1
        id={`${testId}-title`}
        className="text-lg font-semibold text-od-text"
      >
        {title}
      </h1>
      <p className="mt-1 text-sm text-od-muted">{detail}</p>

      <div
        className="mt-5 flex flex-col gap-2"
        role="group"
        aria-label="Ways to connect a Google Sheet"
      >
        <Button
          variant="primary"
          size="md"
          className="w-full justify-center sm:w-auto sm:justify-start"
          onClick={() => onChoose("paste")}
          data-testid="sheets-choice-paste"
        >
          Paste a Sheet link
        </Button>
        <Button
          variant="secondary"
          size="md"
          className="w-full justify-center sm:w-auto sm:justify-start"
          onClick={() => onChoose("browse")}
          data-testid="sheets-choice-browse"
        >
          Choose from Google Drive
        </Button>
        {sampleAvailable && (
          <Button
            variant="secondary"
            size="md"
            className="w-full justify-center sm:w-auto sm:justify-start"
            loading={sampleLoading}
            onClick={() => onChoose("sample")}
            data-testid="sheets-choice-sample"
          >
            Try a sample sheet
          </Button>
        )}
      </div>

      <div className="mt-4">
        <Button
          variant="link"
          size="sm"
          onClick={onMoreConnections}
          className="px-0 text-left text-[13px] underline decoration-od-border underline-offset-4 hover:decoration-od-primary"
          data-testid="more-connections"
        >
          More connections
        </Button>
      </div>
    </section>
  );
};

export default SheetsConnectChoices;
